import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, DesktopWindows as DesktopWindowsIcon } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { DayTimelineBar } from './AuditAttendanceGridLayout';

const formatDurationLabel = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return '0m';
  const total = Math.max(0, Math.floor(Number(seconds)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatDayLabel = (dateKey) => {
  if (!dateKey) return '—';
  try {
    return format(parseISO(`${dateKey}T12:00:00`), 'EEE, MMM dd, yyyy');
  } catch (_) {
    return dateKey;
  }
};

/**
 * Present-style timeline modal for a single workstation desk.
 * Header: workstation name + Total Occupancy (no In/Out chips).
 * Body: DayTimelineBar for the selected calendar day.
 */
const WorkstationTimelineModal = ({
  open,
  onClose,
  workstationLabel,
  summaryDate,
  rangeLabel = '',
  liveDate = null,
  timeline,
  loading = false,
  thumbnail = null,
}) => {
  const theme = useTheme();
  const isMobileModal = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    if (!open) return undefined;
    const interval = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => clearInterval(interval);
  }, [open]);

  const dateKey = timeline?.summary_date || summaryDate || format(new Date(), 'yyyy-MM-dd');
  const isToday = Boolean(liveDate) && dateKey === liveDate;
  const events = useMemo(
    () => (Array.isArray(timeline?.events) ? timeline.events : []),
    [timeline],
  );
  const dayRows = useMemo(() => {
    const days = Array.isArray(timeline?.days) ? timeline.days : [];
    if (days.length < 2) return [];
    return days.filter((day) => Array.isArray(day?.events) && day.events.length > 0);
  }, [timeline]);
  const isMultiDay = Array.isArray(timeline?.days) && timeline.days.length > 1;
  const emptyRangeLabel = rangeLabel || formatDayLabel(dateKey);
  const totalOccupancyLabel = formatDurationLabel(timeline?.total_duration_sec);
  const occupied = String(timeline?.status || '').toLowerCase() === 'occupied';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobileModal}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 200 }}
      PaperProps={{
        sx: {
          borderRadius: isMobileModal ? 0 : 3,
          overflow: 'hidden',
          bgcolor: '#fafbfc',
          maxHeight: isMobileModal ? '100vh' : '85vh',
        },
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Avatar
              src={thumbnail || undefined}
              alt={workstationLabel || 'Workstation'}
              imgProps={{ 'data-no-image-preview': 'true' }}
              sx={{
                width: { xs: 44, sm: 56 },
                height: { xs: 44, sm: 56 },
                bgcolor: '#EEF2FF',
                color: 'primary.main',
                flexShrink: 0,
              }}
            >
              <DesktopWindowsIcon />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  mb: 1,
                }}
              >
                {workstationLabel || 'Workstation'}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={occupied ? 'Occupied' : 'Idle'}
                  sx={{
                    height: { xs: 20, sm: 24 },
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    fontWeight: 600,
                    bgcolor: occupied ? '#dcfce7' : '#f1f5f9',
                    color: occupied ? '#166534' : '#475569',
                  }}
                />
                <Chip
                  size="small"
                  label={`Total Occupancy: ${totalOccupancyLabel}`}
                  sx={{
                    height: { xs: 20, sm: 24 },
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    fontWeight: 600,
                    bgcolor: '#dbeafe',
                    color: '#1d4ed8',
                  }}
                />
                {loading && (
                  <Chip
                    size="small"
                    icon={<CircularProgress size={12} />}
                    label="Loading timeline"
                    sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600 }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' }, ml: 1 }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: '#fafbfc',
          overflowY: 'auto',
          maxHeight: isMobileModal ? 'calc(100vh - 180px)' : 'calc(85vh - 200px)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1, sm: 0 }}
          sx={{ mb: 2.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
              }}
            >
              Daily Timeline
            </Typography>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444' }} />
          </Stack>
          <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 10, sm: 12 }, height: { xs: 5, sm: 6 }, bgcolor: '#86efac', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem' } }}>
                Occupied
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 10, sm: 12 }, height: { xs: 5, sm: 6 }, bgcolor: '#fecaca', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem' } }}>
                Away
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        {loading && events.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <CircularProgress size={28} />
          </Box>
        ) : isMultiDay ? (
          dayRows.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                No occupancy recorded for {emptyRangeLabel}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {dayRows.map((day) => {
                const dayKey = day.summary_date;
                const dayIsToday = Boolean(liveDate) && dayKey === liveDate;
                return (
                  <Box
                    key={dayKey}
                    sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2.5 }}
                  >
                    <DayTimelineBar
                      key={`${dayKey}-${dayIsToday ? currentTime.toISOString().slice(0, 16) : 'past'}`}
                      date={formatDayLabel(dayKey)}
                      events={day.events}
                      isToday={dayIsToday}
                      snapshotCache={{}}
                      showInOutCounts={false}
                      summaryDurationLabel={formatDurationLabel(day.total_duration_sec)}
                      hideMarkers
                    />
                  </Box>
                );
              })}
            </Stack>
          )
        ) : events.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              No occupancy recorded for {emptyRangeLabel}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2.5 }}>
            {/* Remount when the day or live clock tick changes so open segments stay fresh. */}
            <DayTimelineBar
              key={`${dateKey}-${isToday ? currentTime.toISOString().slice(0, 16) : 'past'}`}
              date={formatDayLabel(dateKey)}
              events={events}
              isToday={isToday}
              snapshotCache={{}}
              showInOutCounts={false}
              summaryDurationLabel={totalOccupancyLabel}
              hideMarkers
            />
          </Box>
        )}
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fff', display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default WorkstationTimelineModal;
